import {
  MAX_SPOTLIGHT_CHECKINS_ELIGIBILITY_COUNT,
  MAX_SPOTLIGHT_RECENT_VIEWS_ELIGIBILITY_COUNT,
  MAX_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS,
  MAX_SPOTLIGHT_SEARCH_RADIUS_DEGREES,
  MIN_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS,
  MIN_SPOTLIGHT_SEARCH_RADIUS_DEGREES,
  SPOTLIGHT_ELIGIBILITY_RULE_DESCRIPTIONS,
  SPOTLIGHT_ELIGIBILITY_RULE_LABELS,
  SpotlightEligibilityConfig,
  SpotlightEligibilityRules,
  hasEnabledSpotlightEligibilityRule,
  spotlightSearchRadiusDegreesToMiles,
  spotlightSearchRadiusMilesToDegrees,
} from '@ivy-monorepo/shared'

type SpotlightEligibilityEditorProps = {
  value: SpotlightEligibilityConfig
  onChange: (value: SpotlightEligibilityConfig) => void
}

type CountRuleKey = 'checkedIns' | 'recentViews'
type FlagRuleKey = 'hasLogo' | 'activeSpecial' | 'sponsored'

const MIN_SEARCH_RADIUS_MILES = spotlightSearchRadiusDegreesToMiles(MIN_SPOTLIGHT_SEARCH_RADIUS_DEGREES)
const MAX_SEARCH_RADIUS_MILES = spotlightSearchRadiusDegreesToMiles(MAX_SPOTLIGHT_SEARCH_RADIUS_DEGREES)

const COUNT_RULE_MAX: Record<CountRuleKey, number> = {
  checkedIns: MAX_SPOTLIGHT_CHECKINS_ELIGIBILITY_COUNT,
  recentViews: MAX_SPOTLIGHT_RECENT_VIEWS_ELIGIBILITY_COUNT,
}

const SpotlightEligibilityEditor = ({ value, onChange }: SpotlightEligibilityEditorProps) => {
  const updateRules = (patch: Partial<SpotlightEligibilityRules>) => {
    onChange({
      ...value,
      rules: {
        ...value.rules,
        ...patch,
      },
    })
  }

  const updateCountRule = (key: CountRuleKey, patch: Partial<SpotlightEligibilityRules[CountRuleKey]>) => {
    updateRules({
      [key]: {
        ...value.rules[key],
        ...patch,
      },
    })
  }

  const updateFlagRule = (key: FlagRuleKey, enabled: boolean) => {
    updateRules({
      [key]: { enabled },
    })
  }

  const searchRadiusValid =
    Number.isFinite(value.searchRadiusDegrees)
    && value.searchRadiusDegrees >= MIN_SPOTLIGHT_SEARCH_RADIUS_DEGREES
    && value.searchRadiusDegrees <= MAX_SPOTLIGHT_SEARCH_RADIUS_DEGREES

  const recentViewsWindowValid =
    Number.isInteger(value.recentViewsWindowDays)
    && value.recentViewsWindowDays >= MIN_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS
    && value.recentViewsWindowDays <= MAX_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS

  const hasEnabledRule = hasEnabledSpotlightEligibilityRule(value.rules)

  return (
    <div className="spotlight-eligibility-editor">
      <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
        A venue must be inside the search radius, within its availability window, and match{' '}
        <strong>at least one</strong> enabled rule below. Rules use <strong>OR</strong> logic — a busy venue with
        check-ins qualifies even if it has no logo; a venue with only a logo qualifies if that rule is on.
      </div>
      <div className="ticker-settings-grid">
        <label className="ticker-setting-field">
          <span className="ticker-setting-label">Search radius (miles)</span>
          <input
            type="number"
            className="map-filter-input ticker-setting-count-input"
            min={MIN_SEARCH_RADIUS_MILES}
            max={MAX_SEARCH_RADIUS_MILES}
            step={1}
            value={Math.round(spotlightSearchRadiusDegreesToMiles(value.searchRadiusDegrees) * 10) / 10}
            onChange={e =>
              onChange({
                ...value,
                searchRadiusDegrees: spotlightSearchRadiusMilesToDegrees(Number.parseFloat(e.target.value) || 0),
              })
            }
          />
          <span className="feature-flag-subtitle">
            Half-width of the lat/lng box around the user&apos;s coordinates ({MIN_SEARCH_RADIUS_MILES.toFixed(0)}–
            {MAX_SEARCH_RADIUS_MILES.toFixed(0)} mi, default ≈ 35 mi). Applies to every ticker category — Sponsored,
            Trending, and the eligibility rules below all share this one radius. Does not use driving distance — a
            venue at the corner of the box may be farther than one near the center.
          </span>
        </label>
        <label className="ticker-setting-field">
          <span className="ticker-setting-label">Recent views window (days)</span>
          <input
            type="number"
            className="map-filter-input ticker-setting-count-input"
            min={MIN_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS}
            max={MAX_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS}
            step={1}
            value={value.recentViewsWindowDays}
            onChange={e => onChange({ ...value, recentViewsWindowDays: Number.parseInt(e.target.value, 10) || 0 })}
          />
          <span className="feature-flag-subtitle">
            Unique users who peeked at a venue within this many days count toward the <strong>Recent viewers</strong>{' '}
            rule, populate <code>interestedUsers</code> / <code>peeks</code> on the response, and act as a ranking
            tie-breaker after activity priorities.
          </span>
        </label>
      </div>
      {!searchRadiusValid || !recentViewsWindowValid ? (
        <div className="feature-flag-subtitle ticker-setting-error">
          Check search radius and recent-views window values.
        </div>
      ) : null}
      <table className="map-filters-table spotlight-eligibility-table">
        <thead>
          <tr>
            <th>On</th>
            <th>Rule</th>
            <th>Min count</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {(['checkedIns', 'recentViews'] as const).map(key => (
            <tr key={key}>
              <td>
                <input
                  type="checkbox"
                  checked={value.rules[key].enabled}
                  onChange={e => updateCountRule(key, { enabled: e.target.checked })}
                />
              </td>
              <td>{SPOTLIGHT_ELIGIBILITY_RULE_LABELS[key]}</td>
              <td>
                <input
                  type="number"
                  className="map-filter-input spotlight-eligibility-min-input"
                  min={0}
                  max={COUNT_RULE_MAX[key]}
                  step={1}
                  disabled={!value.rules[key].enabled}
                  value={value.rules[key].minCount}
                  onChange={e => updateCountRule(key, { minCount: Number.parseInt(e.target.value, 10) || 0 })}
                />
              </td>
              <td className="feature-flag-subtitle">{SPOTLIGHT_ELIGIBILITY_RULE_DESCRIPTIONS[key]}</td>
            </tr>
          ))}
          {(['hasLogo', 'activeSpecial', 'sponsored'] as const).map(key => (
            <tr key={key}>
              <td>
                <input
                  type="checkbox"
                  checked={value.rules[key].enabled}
                  onChange={e => updateFlagRule(key, e.target.checked)}
                />
              </td>
              <td>{SPOTLIGHT_ELIGIBILITY_RULE_LABELS[key]}</td>
              <td className="spotlight-eligibility-na">—</td>
              <td className="feature-flag-subtitle">{SPOTLIGHT_ELIGIBILITY_RULE_DESCRIPTIONS[key]}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!hasEnabledRule ? (
        <div className="feature-flag-subtitle ticker-setting-error">
          Enable at least one eligibility rule before saving.
        </div>
      ) : null}
    </div>
  )
}

export default SpotlightEligibilityEditor
